/**
 * Refunds with no signal.
 *
 * Deliberately narrow, because the server cannot check any of this until the
 * money has already left the drawer:
 *
 * - **Only sales rung up on this till**, from its own record. It knows exactly
 *   what it sold and what it has already given back, so it cannot be talked
 *   into refunding somebody else's sale or the same item twice.
 * - **Cash only.** Card and account refunds go back through systems that need
 *   the server.
 * - **Under the shop's offline limit**, per refund. That bounds the one risk
 *   left: another counter refunding the same sale online while this one is
 *   offline. The server catches the double when this uploads, but by then the
 *   cash is gone, and the limit is what that can cost.
 *
 * The server checks it all again on upload (`apps/sync/services._refund`), and
 * anything it refuses lands in the Sync Centre as a conflict for the owner.
 */

import { db, getSyncMeta } from "@/db/database";
import type { LocalRefund, LocalSale } from "@/db/schema";

/** Re-exported so screens need not reach into the local database's types. */
export type { LocalRefund };
import { authRepository } from "@/repositories/authRepository";
import { offlineGrantRepository } from "@/repositories/offlineGrantRepository";
import { localStockRepository } from "@/repositories/localStockRepository";
import { outboxRepository } from "@/repositories/outboxRepository";

export interface RefundableLine {
  lineNumber: number;
  description: string;
  unitPrice: string;
  sold: string;
  remaining: string;
}

export interface RefundableSale {
  id: string;
  receiptNo: string;
  when: string;
  total: string;
  lines: RefundableLine[];
}

export interface LocalRefundInput {
  sale: RefundableSale;
  tillId: string;
  lines: { lineNumber: number; quantity: number; restock: boolean }[];
  reason: string;
  /** A manager approving it, for a cashier who may not refund alone. */
  approver?: { staffCode: string; pin: string };
}

/** Whether the signed-in person needs a manager to approve an offline refund. */
export async function needsManagerOffline(): Promise<boolean> {
  const permissions = (await authRepository.load())?.permissions ?? [];
  if (!permissions.includes("sales.refund")) return true;
  const settings = (await getSyncMeta()).tillSettings;
  return Boolean(settings?.refundsNeedApproval && !permissions.includes("sales.approve_refund"));
}

export class OfflineRefundRefused extends Error {}

/** How far back the till offers its own sales for an offline refund. */
const LOOKBACK_DAYS = 7;

export const localRefundRepository = {
  /** The most one offline refund may give back, or 0 when the shop has them off. */
  async limit(): Promise<number> {
    const settings = (await getSyncMeta()).tillSettings;
    return settings ? Number(settings.offlineRefundLimit) : 0;
  },

  /** This till's own cash sales from the last week with anything left to give back. */
  async refundableSales(): Promise<RefundableSale[]> {
    const since = new Date(Date.now() - LOOKBACK_DAYS * 24 * 3600_000).toISOString();
    const sales = (await db.pendingSales.toArray())
      .filter((sale) => sale.clientCreatedAt >= since)
      .filter((sale) => sale.payments.length > 0 && sale.payments.every((p) => p.method === "CASH"))
      .sort((a, b) => b.clientCreatedAt.localeCompare(a.clientCreatedAt));

    const refunds = await db.pendingRefunds.toArray();
    return sales
      .map((sale) => withRemaining(sale, refunds))
      .filter((sale) => sale.lines.some((line) => Number(line.remaining) > 0));
  },

  /**
   * Give cash back, offline. Stored, queued and taken off the local stock in one
   * transaction, so a refund cannot exist on the till without being on its way.
   */
  async refund(input: LocalRefundInput): Promise<LocalRefund> {
    const session = await authRepository.load();
    const meta = await getSyncMeta();
    const settings = meta.tillSettings;
    const needsManager = await needsManagerOffline();

    const byNumber = new Map(input.sale.lines.map((line) => [line.lineNumber, line]));
    let total = 0;
    for (const chosen of input.lines) {
      const line = byNumber.get(chosen.lineNumber);
      if (!line || chosen.quantity <= 0 || chosen.quantity > Number(line.remaining)) {
        throw new OfflineRefundRefused("That is more than is left to give back.");
      }
      total += Number(line.unitPrice) * chosen.quantity;
    }

    const limit = settings ? Number(settings.offlineRefundLimit) : 0;
    if (limit <= 0) {
      throw new OfflineRefundRefused("This shop does not give refunds without signal.");
    }
    if (total > limit) {
      throw new OfflineRefundRefused(
        `R${total.toFixed(2)} is over this shop's limit of R${limit.toFixed(2)} for a refund with no signal.`,
      );
    }

    // Last, after everything that can be checked without a PIN, so a refund
    // that was never going to be allowed does not cost a manager an attempt.
    let approvedBy: string | null = null;
    if (needsManager) approvedBy = await approveOffline(input.approver);

    const refund: LocalRefund = {
      id: crypto.randomUUID(),
      saleId: input.sale.id,
      receiptNo: input.sale.receiptNo,
      lines: input.lines.map((line) => ({
        lineNumber: line.lineNumber,
        quantity: String(line.quantity),
        restock: line.restock,
      })),
      total: total.toFixed(2),
      reason: input.reason.trim(),
      createdAt: new Date().toISOString(),
    };

    const sale = await db.pendingSales.get(input.sale.id);

    await db.transaction(
      "rw",
      [db.pendingRefunds, db.outbox, db.syncMeta, db.stockLevels],
      async () => {
        await db.pendingRefunds.add(refund);
        await outboxRepository.enqueue({
          type: "refund.create",
          entityId: refund.id,
          idempotencyKey: refund.id,
          businessDate: refund.createdAt.slice(0, 10),
          payload: {
            ...(session?.user.id ? { cashier_id: session.user.id } : {}),
            refund_id: refund.id,
            sale_id: input.sale.id,
            till_id: input.tillId,
            lines: input.lines.map((line) => ({
              line_number: line.lineNumber,
              quantity: String(line.quantity),
              restock: line.restock,
            })),
            reason: refund.reason,
            // Checked on this till against the manager's offline grant. The
            // server takes it only from this paired till, and checks again
            // that the manager may refund and approve (`sync/services._refund`).
            ...(approvedBy ? { approved_by_id: approvedBy } : {}),
          },
        });

        // Back on the shelf goes back in the local count straight away, the way
        // a sale comes off it — written to `pendingDelta`, which the next pull
        // leaves alone until the server's own figure includes it.
        if (!sale) return;
        for (const chosen of input.lines) {
          if (!chosen.restock) continue;
          const productId = sale.lines[chosen.lineNumber - 1]?.productId;
          if (!productId) continue;
          // Back to the counter it was sold from, which is where the server
          // restocks it too.
          await localStockRepository.move(sale.sectionId ?? null, productId, chosen.quantity);
        }
      },
    );

    return refund;
  },
};

/**
 * A refund this till gave **with** signal, on one of its own sales.
 *
 * Recorded so the offline list knows it: otherwise a sale refunded online at
 * nine was offered again in full at ten when the signal dropped, and the second
 * refund was only caught on upload, after the cash had gone. Not queued — the
 * server already has it — and the stock is left alone, because the server's
 * own figure already includes it and the next pull brings that down.
 */
export async function recordOnlineRefund(input: {
  saleId: string;
  lines: { lineNumber: number; quantity: number }[];
  total: string;
  reason: string;
}): Promise<void> {
  const sale = await db.pendingSales.get(input.saleId);
  if (!sale || input.lines.length === 0) return;

  await db.pendingRefunds.add({
    id: crypto.randomUUID(),
    saleId: input.saleId,
    receiptNo: sale.receiptNo,
    lines: input.lines.map((line) => ({
      lineNumber: line.lineNumber,
      quantity: String(line.quantity),
      restock: false,
    })),
    total: input.total,
    reason: input.reason,
    createdAt: new Date().toISOString(),
  });
}

/**
 * A manager's approval with no signal: their staff code and PIN, checked
 * against the grant they hold on this till from signing in here with signal.
 * The same check, lockout and all, as an offline sign-in — but nobody is signed
 * in by it. Returns the manager's id.
 */
async function approveOffline(approver: LocalRefundInput["approver"]): Promise<string> {
  if (!approver?.staffCode || !approver.pin) {
    throw new OfflineRefundRefused("A manager has to approve this refund with their code and PIN.");
  }

  const check = await offlineGrantRepository.check(approver.staffCode, approver.pin);
  if (!check.ok) {
    if (check.reason === "LOCKED") {
      throw new OfflineRefundRefused("That manager's PIN is locked after too many tries.");
    }
    if (check.reason === "NO_GRANT" || check.reason === "EXPIRED") {
      throw new OfflineRefundRefused(
        "That manager has not signed in on this till with signal recently, so it cannot check their PIN offline.",
      );
    }
    throw new OfflineRefundRefused("That manager code and PIN cannot approve this refund.");
  }

  const permissions = check.session.permissions;
  if (!permissions.includes("sales.refund") || !permissions.includes("sales.approve_refund")) {
    throw new OfflineRefundRefused("That manager code and PIN cannot approve this refund.");
  }
  return check.session.user.id;
}

function withRemaining(sale: LocalSale, refunds: LocalRefund[]): RefundableSale {
  const given = new Map<number, number>();
  for (const refund of refunds) {
    if (refund.saleId !== sale.id) continue;
    for (const line of refund.lines) {
      given.set(line.lineNumber, (given.get(line.lineNumber) ?? 0) + Number(line.quantity));
    }
  }

  return {
    id: sale.id,
    receiptNo: sale.receiptNo,
    when: sale.clientCreatedAt,
    total: sale.total,
    // The server numbers a sale's lines from 1 in the order the till sent them,
    // which is the order they are stored here.
    lines: sale.lines.map((line, index) => {
      const lineNumber = index + 1;
      const remaining = Number(line.quantity) - (given.get(lineNumber) ?? 0);
      return {
        lineNumber,
        description: line.productNameSnapshot,
        unitPrice: line.unitPrice,
        sold: line.quantity,
        remaining: String(Math.max(remaining, 0)),
      };
    }),
  };
}
